import { inject, Injectable, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { firstValueFrom, map } from "rxjs";

import { ConfirmDialogComponent } from "@components/shared";
import {
  ConfirmDialogResponseEnum,
  CustomFieldsResourceTypeEnum,
  CustomFieldsTypeEnum,
} from "@shared/enums";
import { ICustomField } from "@shared/interfaces";
import { CustomFieldsService, NotificationService } from "@shared/services";
import { LocalizationUtils } from "@shared/utils";

@Injectable({
  providedIn: "root",
})
export class SettingsEudrService {
  missingCustomFieldGroups: {
    resourceType: CustomFieldsResourceTypeEnum;
    labels: string[];
  }[] = [];

  private requiredCustomFieldsGroups: {
    resourceType: CustomFieldsResourceTypeEnum;
    labels: string[];
  }[] = [];

  allCustomFields: ICustomField[] = [];

  isLoading = signal(false);

  private readonly customFieldsService = inject(CustomFieldsService);

  private readonly dialog = inject(MatDialog);

  private readonly notificationService = inject(NotificationService);

  reset() {
    this.missingCustomFieldGroups = [];
    this.allCustomFields = [];
    this.requiredCustomFieldsGroups = [];
    this.isLoading.set(false);
  }

  async setMissingCustomFields(): Promise<void> {
    this.missingCustomFieldGroups = [];
    this.allCustomFields = await this.customFieldsService.getAll();

    this.requiredCustomFieldsGroups = [];

    if (!this.requiredCustomFieldsGroups.length) {
      return;
    }

    for (const group of this.requiredCustomFieldsGroups) {
      for (const label of group.labels) {
        if (
          !this.allCustomFields.some(
            (cf) => cf.resourceTypes.includes(group.resourceType) && cf.label === label,
          )
        ) {
          this.addMissingCustomField(group.resourceType, label);
        }
      }
    }
  }

  private addMissingCustomField(resourceType: CustomFieldsResourceTypeEnum, label: string): void {
    const group = this.missingCustomFieldGroups.find((m) => m.resourceType === resourceType);

    if (group) {
      group.labels.push(label);
    } else {
      this.missingCustomFieldGroups.push({ resourceType, labels: [label] });
    }
  }

  async onOpenMissingCustomFieldsDialog(
    shouldSetMissingCustomFields: boolean = false,
  ): Promise<void> {
    if (shouldSetMissingCustomFields) {
      await this.setMissingCustomFields();
    }

    if (!this.missingCustomFieldGroups.length) {
      return;
    }

    let contentListHtml = "";

    for (const group of this.missingCustomFieldGroups) {
      let labelsHtml = "";

      for (const label of group.labels) {
        labelsHtml += `<li>${label}</li>`;
      }
      contentListHtml += `<li>${LocalizationUtils.getEntityType(group.resourceType)}<ul>${labelsHtml}</ul></li>`;
    }

    const contentHTML = $localize`:@@action.settings-eudr.addcustomfieldscontent:The following custom fields will be added to the corresponding modules to ensure proper functioning of the EUDR integration:<br><ul>${contentListHtml}</ul>`;

    const confirmDialogOptions = {
      data: {
        title: $localize`:@@action.settings-eudr.addcustomfields:Add custom fields`,
        contentHTML,
        confirmButtonText: $localize`:@@action.add:Add`,
      },
    };

    const isConfirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, confirmDialogOptions)
        .afterClosed()
        .pipe(
          map((result: ConfirmDialogResponseEnum) => result === ConfirmDialogResponseEnum.CONFIRM),
        ),
    );

    if (!isConfirmed) {
      return;
    }

    this.isLoading.set(true);

    try {
      const promises: Promise<ICustomField>[] = [];

      for (const group of this.missingCustomFieldGroups) {
        for (const label of group.labels) {
          const existingCustomField = this.allCustomFields.find((cf) => cf.label === label);
          const resourceTypes = existingCustomField ? existingCustomField.resourceTypes : [];

          resourceTypes.push(group.resourceType);
          const type = existingCustomField?.type ?? CustomFieldsTypeEnum.TEXT;

          promises.push(
            this.customFieldsService.createOrUpdate(
              {
                id: existingCustomField?.id ?? undefined,
                label,
                resourceTypes,
                type,
              },
              existingCustomField?.id ?? undefined,
            ),
          );
        }
      }

      await Promise.all(promises);
      this.notificationService.showSuccess(
        $localize`:@@settings-eudr.customfieldsadded:Custom fields added`,
      );
    } catch (error) {
      this.notificationService.showError(error);
    } finally {
      await this.setMissingCustomFields();
      this.isLoading.set(false);
    }
  }
}
